// use-projects-menu-service.ts
import * as React from 'react';
import type { OPEN_FOR, ProjectResponse } from '@/types';
import { useTenant } from './use-tenant';
import type { ConfirmDialogConfig } from '@/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useAssignInternalProjectMutation,
  useDeleteExternalProjectMutation,
  useDeleteInternalProjectMutation,
  useDuplicateInternalProjectMutation,
  useGetCurrentUserQuery,
  useUpdateInternalProjectThumbnailMutation,
  useUpdateThumbnailOfExternalProjectMutation,
  useAcceptPublicProjectMutation,
} from '@/state';
import { toast } from 'sonner';
import { getMessageByStatus } from '@/lib';

export interface UseProjectMenuServiceProps {
  projectType: 'INTERNAL' | 'EXTERNAL';
  assignedInternalProjects?: boolean;
}

export function useProjectsMenuService({
  projectType,
  assignedInternalProjects,
}: UseProjectMenuServiceProps) {
  const { tenant } = useTenant();

  // per-project dropdown open (store project.id)
  const [openMenuId, setOpenMenuId] = React.useState<number | null>(null);

  // update dialog
  const [showUpdateProject, setShowUpdateProject] = React.useState(false);
  const [selectedProject, setSelectedProject] =
    React.useState<ProjectResponse | null>(null);
  const [openFor, setOpenFor] = React.useState<OPEN_FOR>('create');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // thumbnail editor dialog
  const [showDialog, setShowDialog] = React.useState(false);
  const [image, setImage] = React.useState('');
  const [imageFile, setImageFile] = React.useState<File | null>(null);

  // confirm dialog
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmConfig, setConfirmConfig] =
    React.useState<ConfirmDialogConfig | null>(null);

  // assign mentor sheet
  const [mentorSheetProject, setMentorSheetProject] =
    React.useState<ProjectResponse | null>(null);

  // mutations
  const [deleteInternalProject, { isLoading: deleteInternalLoading }] =
    useDeleteInternalProjectMutation();

  const [
    updateInternalProjectThumbnail,
    { isLoading: thumbnailLoading, error: thumbnailError },
  ] = useUpdateInternalProjectThumbnailMutation();

  const [duplicateInternalProject, { isLoading: duplicateLoading }] =
    useDuplicateInternalProjectMutation();

  const [assignInternalProject, { isLoading: assignLoading }] =
    useAssignInternalProjectMutation();

  const [
    updateExternalProjectThumbnail,
    { isLoading: updateThumbnailExternalLoading },
  ] = useUpdateThumbnailOfExternalProjectMutation();

  const [deleteExternalProject, { isLoading: deleteExternalLoading }] =
    useDeleteExternalProjectMutation();

  const [acceptPublicProject, { isLoading: acceptPublicProjectLoading }] =
    useAcceptPublicProjectMutation();

  // query
  const { data: currentUser } = useGetCurrentUserQuery({ tenant: tenant! });

  // --------- handlers ----------

  const handleEdit = (project: ProjectResponse) => {
    setOpenMenuId(null);
    setSelectedProject(project);
    setOpenFor('update');
    setShowUpdateProject(true);
  };

  const handleThumbnailChange = React.useCallback(
    (project: ProjectResponse) => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.onchange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target?.files && target.files[0]) {
          const file = target.files[0];
          const imageURL = URL.createObjectURL(file);
          setImageFile(file);
          setImage(imageURL);
          setShowDialog(true);
          setSelectedProject(project);
        }
      };
      fileInput.click();
    },
    []
  );

  const handleDelete = (
    project: ProjectResponse,
    from?: 'details' | 'board'
  ) => {
    setOpenMenuId(null);
    setSelectedProject(project);

    setConfirmConfig({
      title: 'Delete project?',
      description: `This will permanently delete "${project.name}". This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true,
      onConfirm: async () => {
        try {
          if (projectType === 'INTERNAL') {
            await deleteInternalProject({
              tenant: tenant!,
              projectId: project.id,
            }).unwrap();
          } else {
            await deleteExternalProject({
              tenant: tenant!,
              projectId: project.id,
            }).unwrap();
          }
          toast.success('Project deleted successfully');
          if (from === 'details') {
            navigate(-1);
          }
        } catch (error: any) {
          toast.error('Error', {
            description: getMessageByStatus(error?.status || 500),
          });
        } finally {
          setConfirmOpen(false);
          setSelectedProject(null);
        }
      },
    });

    setConfirmOpen(true);
  };

  const updateThumbnailBanner = React.useCallback(async () => {
    try {
      if (selectedProject && image && imageFile) {
        const projectBannerAsBinary = new FormData();
        projectBannerAsBinary.append('project_banner', imageFile);

        if (projectType === 'INTERNAL') {
          await updateInternalProjectThumbnail({
            tenant: tenant!,
            projectId: selectedProject.id,
            data: projectBannerAsBinary,
            assignedInternalProjects: !!assignedInternalProjects,
          }).unwrap();
        } else if (projectType === 'EXTERNAL') {
          const externalProjectId = selectedProject.project_id ?? selectedProject.id;
          const resourceId = selectedProject.project_id ? selectedProject.id : undefined;
          await updateExternalProjectThumbnail({
            tenant: tenant!,
            projectId: externalProjectId,
            data: projectBannerAsBinary,
            resourceId,
          }).unwrap();
        } else {
          toast.message('Project type not supported');
        }

        toast.success('Project banner updated successfully.');
        return;
      }

      toast.info('Please select an image or project first.');
    } catch (error: any) {
      toast.error('Error', {
        description: getMessageByStatus(error?.status || 500),
      });
    } finally {
      setShowDialog(false);
      setImage('');
      setImageFile(null);
    }
  }, [
    selectedProject,
    image,
    imageFile,
    tenant,
    projectType,
    updateInternalProjectThumbnail,
    updateExternalProjectThumbnail,
    assignedInternalProjects,
  ]);

  const handleDuplicate = React.useCallback(
    async (project: ProjectResponse) => {
      if (projectType === 'INTERNAL') {
        await duplicateInternalProject({
          tenant: tenant!,
          projectId: project.id,
        }).unwrap();
        toast.success('Project duplicated.');
      }
    },
    [duplicateInternalProject, projectType, tenant]
  );

  const handleAssignMentor = React.useCallback(
    async (mentorId: string, mentorName: string) => {
      if (!mentorSheetProject) return;

      if (projectType === 'INTERNAL') {
        await assignInternalProject({
          tenant: tenant!,
          projectId: mentorSheetProject.id,
          clientMentorId: mentorId,
        }).unwrap();
        
        const id = searchParams.get('projectResourcesId');

        if (id && !isNaN(Number(id))) {
          const newId = Number(id) + 1;

          const params = new URLSearchParams(searchParams.toString());
          params.set('projectResourcesId', String(newId));

          navigate(`?${params.toString()}`);
        }
        const fullName = `${currentUser?.profile?.first_name} ${currentUser?.profile?.last_name}`;
        toast.message(
          `${
            mentorName === fullName ? 'You' : mentorName
          } has been assigned to ${mentorSheetProject.name}`
        );
      } else if (projectType === 'EXTERNAL') {
        const res = await acceptPublicProject({
          data: {
            assigned_to: mentorId,
            created_by: currentUser?.id as string,
            project_ref: mentorSheetProject.id,
            public_project_candidates: [{ role: 1, candidate: mentorId }],
          },
        }).unwrap();

        if (res?.id) {
          const templateId = res.project_ref?.id || res.project_ref || mentorSheetProject.id;
          const assignedId = res.id;

          navigate(
            `/${tenant}/projects/${templateId}?projectResourcesId=${assignedId}&projectId=${assignedId}&type=EXTERNAL`,
            { replace: true }
          );
        }

        const fullName = `${currentUser?.profile?.first_name} ${currentUser?.profile?.last_name}`;
        toast.message(
          `${
            mentorName === fullName ? 'You' : mentorName
          } has been successfully assigned to ${mentorSheetProject.name}`
        );
      }

      setMentorSheetProject(null);
    },
    [
      assignInternalProject,
      acceptPublicProject,
      projectType,
      mentorSheetProject,
      tenant,
      currentUser,
      navigate,
      searchParams,
    ]
  );

  const handleProjectClick = React.useCallback(
    (
      projectId: number,
      projectResourcesId?: number,
      realProjectId?: number
    ) => {
      navigate(
        `${location.pathname}/${projectId}?projectResourcesId=${projectResourcesId ?? ''}&projectId=${realProjectId ?? projectId}&type=${projectType}`,
        { relative: 'path' }
      );
    },
    [navigate, location, projectType]
  );

  // loading helpers
  const deletingLoading = deleteInternalLoading || deleteExternalLoading;

  return {
    // state
    openMenuId,
    setOpenMenuId,
    showUpdateProject,
    setShowUpdateProject,
    selectedProject,
    setSelectedProject,
    openFor,
    setOpenFor,

    showDialog,
    setShowDialog,
    image,
    imageFile,

    confirmOpen,
    setConfirmOpen,
    confirmConfig,
    setConfirmConfig,

    mentorSheetProject,
    setMentorSheetProject,

    currentUser,

    // loading / error
    deletingLoading,
    thumbnailLoading,
    duplicateLoading,
    assignLoading: assignLoading || acceptPublicProjectLoading,
    updateThumbnailExternalLoading,
    thumbnailError,

    // handlers
    handleEdit,
    handleThumbnailChange,
    handleDelete,
    updateThumbnailBanner,
    handleDuplicate,
    handleAssignMentor,
    handleProjectClick,
  };
}
